import type { QuickBooksFpaMlForecastVsActualRow } from '../../../data/ServerApiClient'

export type ForecastAccuracyStatementSummary = {
  statementType: string
  medianAbsVariancePct: number
  wape: number | null
  bias: number | null
  monthCount: number
}

export type ForecastAccuracySummary = {
  byStatement: ForecastAccuracyStatementSummary[]
  intervalHitRate: number | null
  plainLanguage: string | null
  suppressedReason: string | null
}

export type ForecastAccuracyOptions = {
  recentMonths?: number
  readinessStatus?: string | null
  basisLabels?: string[] | null
}

const STATEMENT_ACCURACY_LABELS: Record<string, string> = {
  profit_and_loss: 'P&L',
  cash_flow: 'cash flow',
  balance_sheet: 'balance sheet',
}

export const forecastAccuracyStatementLabel = (statementType: string): string =>
  STATEMENT_ACCURACY_LABELS[statementType] || statementType.replace(/_/g, ' ')

const medianValue = (values: number[]): number | null => {
  const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b)
  if (!sorted.length) return null
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

const accuracyPctText = (ratio: number): string => {
  const pct = ratio * 100
  return pct >= 10 ? String(Math.round(pct)) : String(Math.round(pct * 10) / 10)
}

/**
 * Summarizes forecast-vs-actual rows into a per-statement accuracy read over
 * the most recent closed months. WAPE is the headline fleet metric; median
 * |variance_pct| stays available as the small-denominator secondary read.
 * Returns plainLanguage null when no actualized rows exist or readiness says
 * the package/period cannot support a clean accuracy claim.
 */
export const forecastAccuracySummary = (
  rows: QuickBooksFpaMlForecastVsActualRow[] | null | undefined,
  opts: ForecastAccuracyOptions = {},
): ForecastAccuracySummary => {
  const recentMonths = Math.max(1, Math.floor(opts.recentMonths ?? 3))
  const basisLabels = new Set((opts.basisLabels || []).map((label) => String(label)))
  const suppressedReason = opts.readinessStatus === 'blocked' || basisLabels.has('books_stale')
    ? 'books_stale'
    : opts.readinessStatus === 'partial' || basisLabels.has('books_incomplete') || basisLabels.has('latest_month_excluded')
      ? 'books_incomplete'
      : null
  const actualized = (rows || []).filter((row) =>
    row.actual_value != null
    && row.variance_pct != null
    && Number.isFinite(Number(row.variance_pct)))
  if (!actualized.length) return { byStatement: [], intervalHitRate: null, plainLanguage: null, suppressedReason }

  const monthOf = (row: QuickBooksFpaMlForecastVsActualRow): string => String(row.period_start || '').slice(0, 7)
  const months = [...new Set(actualized.map(monthOf).filter(Boolean))].sort()
  const windowMonths = new Set(months.slice(-recentMonths))
  const scoped = actualized.filter((row) => windowMonths.has(monthOf(row)))
  if (!scoped.length) return { byStatement: [], intervalHitRate: null, plainLanguage: null, suppressedReason }

  const buckets = new Map<string, { variances: number[]; months: Set<string>; absErr: number; absActual: number; signedErr: number }>()
  scoped.forEach((row) => {
    const statementType = String(row.statement_type || 'unknown')
    const bucket = buckets.get(statementType) || { variances: [], months: new Set<string>(), absErr: 0, absActual: 0, signedErr: 0 }
    const actual = Number(row.actual_value)
    const forecast = Number(row.forecast_value)
    bucket.variances.push(Math.abs(Number(row.variance_pct)))
    bucket.months.add(monthOf(row))
    if (Number.isFinite(actual) && Number.isFinite(forecast)) {
      bucket.absErr += Math.abs(forecast - actual)
      bucket.absActual += Math.abs(actual)
      bucket.signedErr += forecast - actual
    }
    buckets.set(statementType, bucket)
  })
  const byStatement = [...buckets.entries()]
    .map(([statementType, bucket]) => ({
      statementType,
      medianAbsVariancePct: medianValue(bucket.variances) ?? 0,
      wape: bucket.absActual > 0.01 ? bucket.absErr / bucket.absActual : null,
      bias: bucket.absActual > 0.01 ? bucket.signedErr / bucket.absActual : null,
      monthCount: bucket.months.size,
    }))
    .sort((a, b) => b.monthCount - a.monthCount || a.statementType.localeCompare(b.statementType))

  const intervalScored = scoped.filter((row) => row.interval_hit === true || row.interval_hit === false)
  const intervalHitRate = intervalScored.length
    ? intervalScored.filter((row) => row.interval_hit === true).length / intervalScored.length
    : null

  const lead = byStatement[0]
  const monthCount = windowMonths.size
  const plainLanguage = suppressedReason
    ? null
    : `Last ${monthCount} closed month${monthCount === 1 ? '' : 's'}: `
      + `${forecastAccuracyStatementLabel(lead.statementType)} WAPE ${lead.wape != null ? accuracyPctText(lead.wape) : accuracyPctText(lead.medianAbsVariancePct)}%`
      + (intervalHitRate != null ? ` · ${Math.round(intervalHitRate * 100)}% of actuals fell inside the range.` : '.')

  return { byStatement, intervalHitRate, plainLanguage, suppressedReason }
}

export type ForecastBandCalibrationSource = 'calibrated' | 'estimated'

export const forecastBandSource = (validationJson: any): ForecastBandCalibrationSource =>
  validationJson?.band_calibration === 'empirical' ? 'calibrated' : 'estimated'

export type ForecastReadiness = { status: string; basisLabels: string[] }

const trimReadinessStr = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')
const readinessStringList = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean) : [])

/**
 * The single source of truth for forecast data-readiness status + basis labels,
 * extracted from a package's validation_json / data_coverage_json. Used by the page,
 * the export orchestrator, and the headless script so accuracy claims, the provisional
 * banner, and the suppression logic all agree and never overstate.
 */
export const readinessFromValidation = (
  validationJson: any,
  dataCoverageJson?: any,
  packageStatus?: string | null,
): ForecastReadiness => {
  const vj = validationJson || {}
  const dc = dataCoverageJson || {}
  const dataQuality = vj?.readiness?.gates?.data_quality || vj?.gates?.data_quality || dc?.data_quality || null
  return {
    status: trimReadinessStr(dataQuality?.status || vj?.readiness?.status || vj?.status || packageStatus || ''),
    basisLabels: [
      ...readinessStringList(dataQuality?.basis_labels),
      ...readinessStringList(vj?.readiness?.basis_labels),
      ...readinessStringList(dc?.data_quality?.basis_labels),
    ],
  }
}
